import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, CheckCheck } from 'lucide-react';
import { InboxMessage } from '../types/api';
import { apiService } from '../services/api';
import { urlParams } from '../utils/urlParams';
import { AppHeader } from './AppHeader';

const PAGE_SIZE = 20;

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day');
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getPreview(body: string): string {
  const plainTextBody = body.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');

  if (plainTextBody.length <= 140) {
    return plainTextBody;
  }
  return `${plainTextBody.slice(0, 137).trimEnd()}...`;
}

interface InboxViewProps {
  onInboxChanged?: () => void;
}

export const InboxView: React.FC<InboxViewProps> = ({ onInboxChanged }) => {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = useCallback(async (skip = 0, append = false) => {
    const setBusy = append ? setLoadingMore : setLoading;
    setBusy(true);
    if (!append) {
      setError(null);
    }

    try {
      const response = await apiService.getInboxMessages(skip, PAGE_SIZE);
      setMessages((current) => append ? [...current, ...response.messages] : response.messages);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inbox');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await apiService.markAllInboxRead();
      setMessages((current) => current.map((message) => ({ ...message, is_read: true })));
      onInboxChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark inbox as read');
    } finally {
      setMarkingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle size={48} className="text-red-400" />
        <p className="text-gray-700 text-center">{error}</p>
        <button
          onClick={() => loadMessages()}
          className="text-sm text-[#132B64] underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const hasUnread = messages.some((message) => !message.is_read);
  const hasMore = messages.length < total;

  return (
    <div className="min-h-screen bg-white">
      <AppHeader onLogoClick={() => urlParams.resetToHome()} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 lg:pb-8">
        <div className="w-full max-w-[320px] lg:max-w-[620px] mx-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#132B64]">Inbox</h1>
              {total > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {total} message{total === 1 ? '' : 's'}
                </p>
              )}
            </div>

            {hasUnread && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAll}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-full bg-gray-100 text-[#132B64] hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {error && messages.length > 0 && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {messages.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="h-12 w-12 mx-auto text-gray-400" />
              <p className="mt-4 text-sm text-gray-600">No messages yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <button
                  key={message.id}
                  onClick={() => urlParams.navigateToInboxMessage(message.id)}
                  className="w-full text-left bg-[#E7EEE3] rounded-[20px] shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {!message.is_read && (
                          <span className="h-2.5 w-2.5 rounded-full bg-red-500 shrink-0 mt-0.5" />
                        )}
                        <h3 className={`text-sm leading-tight truncate ${message.is_read ? 'font-semibold text-[#132B64]' : 'font-bold text-[#132B64]'}`}>
                          {message.title}
                        </h3>
                      </div>
                      <p className="mt-2 text-sm text-gray-600 leading-5">
                        {getPreview(message.body)}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">
                      {formatRelativeDate(message.created_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {hasMore && (
            <div className="pt-5 flex justify-center">
              <button
                onClick={() => loadMessages(messages.length, true)}
                disabled={loadingMore}
                className="bg-gray-100 text-[#132B64] rounded-full px-6 py-3 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : 'Show more'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
